// Multi-entry tag input for the variant `spec` field. The stored/transmitted value stays a
// single joined string (`spec: string`, unchanged BE contract) — this component only owns the
// UI boundary, converting to/from chips via splitSpec/joinSpec (see spec-tags.ts). Modeled on
// components/multi-value-input/multi-value-input.tsx, which does the same chip-input dance for
// a string[] value; this one differs by working off (and emitting) the joined string directly
// so the three call sites don't need to touch their `spec: string` models at all.
import { Box, Flex, Input, Tag, TagCloseButton, TagLabel } from '@elkaso-app/web-design';
import { useGetSpecValueSuggestionsApi } from 'apis/master-products';
import { useEffect, useMemo, useRef, useState } from 'react';
import { joinSpec, splitSpec } from './spec-tags';

export interface SpecTagsInputProps {
  value: string;
  onChange: (joined: string) => void;
  size?: 'sm';
  placeholder?: string;
  isDisabled?: boolean;
  bg?: string;
}

export const SpecTagsInput = ({ value, onChange, size, placeholder, isDisabled, bg }: SpecTagsInputProps) => {
  const chips = splitSpec(value);
  const [draft, setDraft] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Autocomplete from the distinct spec segments already in the database
  // (GET cms/master-products/spec-values). The draft is debounced so a typing
  // burst issues one request; React Query dedupes/caches per prefix.
  const [debouncedDraft, setDebouncedDraft] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedDraft(draft.trim()), 250);
    return () => clearTimeout(timer);
  }, [draft]);

  const { data: remoteSuggestions } = useGetSpecValueSuggestionsApi(debouncedDraft, {
    enabled: !isDisabled && isFocused,
  });
  const suggestions = useMemo(() => {
    const existing = new Set(chips.map((chip) => chip.toLowerCase()));
    return (remoteSuggestions ?? []).filter((suggestion) => !existing.has(suggestion.toLowerCase()));
  }, [remoteSuggestions, chips]);
  const showSuggestions = isFocused && !!draft.trim() && !!debouncedDraft && suggestions.length > 0;

  // Appends `text` (which may itself contain one or more ';'-delimited parts, e.g. a paste) to
  // the existing chips, then re-splits the whole joined thing through splitSpec — that single
  // call gives us trim + drop-empty + case-sensitive dedupe-keep-first + order-preservation for
  // free, so a single commit() covers both "commit one typed tag" and "paste a bunch of
  // semicolon-separated text" without separate merge logic.
  const commit = (text: string) => {
    if (!text.trim()) return;
    const merged = splitSpec([...chips, text].join(';'));
    onChange(joinSpec(merged));
    setDraft('');
  };

  const removeChip = (item: string) => {
    onChange(joinSpec(chips.filter((c) => c !== item)));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    // A single typed ';' is caught (and preventDefault'd) in onKeyDown before it ever reaches
    // here, so a ';' showing up in an onChange value means pasted text — commit it immediately
    // rather than letting a literal semicolon sit in the draft.
    if (next.includes(';')) {
      commit(next);
      return;
    }
    setDraft(next);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Only preventDefault when this Enter actually commits a chip — an empty draft has
      // nothing to commit, so let the keystroke bubble to the grid row's last-row
      // Enter-append handler as before. See variant-row.tsx's Tr onKeyDown
      // (`!e.defaultPrevented` guard).
      if (draft.trim()) {
        e.preventDefault();
        commit(draft);
      }
      return;
    }
    if (e.key === ';') {
      // ';' is the delimiter, never a literal character in a chip — always swallow the
      // keystroke, committing whatever draft (if any) preceded it.
      e.preventDefault();
      commit(draft);
      return;
    }
    if (e.key === 'Backspace' && !draft && chips.length > 0) {
      removeChip(chips[chips.length - 1]);
    }
  };

  return (
    <Box position='relative'>
      {/* One input-styled container holds chips AND the free-text input (the
          react-select multi convention) — chips above the input made the Spec
          column taller than its row siblings on every spec-editing surface.
          Handlers, commit semantics and the suggestions dropdown are untouched:
          this component owns only the UI boundary (see header). */}
      <Flex
        data-testid='spec-tags-control'
        wrap='wrap'
        align='center'
        gap='1'
        borderWidth='1px'
        borderColor='gray.200'
        rounded='md'
        bg={bg}
        minH={size === 'sm' ? 8 : 10}
        px='2'
        py='1'
        cursor={isDisabled ? 'not-allowed' : 'text'}
        opacity={isDisabled ? 0.4 : undefined}
        pointerEvents={isDisabled ? 'none' : undefined}
        _focusWithin={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)' }}
        _hover={{ borderColor: 'gray.300' }}
        onClick={() => inputRef.current?.focus()}>
        {chips.map((item) => (
          <Tag key={item} size={size ?? 'sm'} flexShrink={0}>
            <TagLabel>{item}</TagLabel>
            {!isDisabled && <TagCloseButton aria-label={`remove-${item}`} onClick={() => removeChip(item)} />}
          </Tag>
        ))}
        <Input
          ref={inputRef}
          variant='unstyled'
          flex='1'
          minW='80px'
          h='auto'
          size={size}
          isDisabled={isDisabled}
          placeholder={placeholder}
          value={draft}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          // Blur must commit synchronously: a Save click blurs this input before the click
          // handler reads form state, so the onChange fired here needs to land before that read
          // — commit() calls onChange directly (no async/debounce), so it does.
          onBlur={() => {
            setIsFocused(false);
            commit(draft);
          }}
        />
      </Flex>
      {showSuggestions && (
        <Box
          position='absolute'
          top='100%'
          left='0'
          right='0'
          mt='1'
          zIndex='dropdown'
          bg='white'
          borderWidth='1px'
          borderColor='gray.200'
          rounded='md'
          shadow='md'
          maxH='180px'
          overflowY='auto'>
          {suggestions.map((suggestion) => (
            <Box
              key={suggestion}
              px='3'
              py='1.5'
              fontSize='sm'
              cursor='pointer'
              _hover={{ bg: 'gray.50' }}
              // onMouseDown (not onClick) + preventDefault: a click's mousedown would
              // otherwise blur the input first, committing the half-typed draft as its
              // own chip before the suggestion lands.
              onMouseDown={(e) => {
                e.preventDefault();
                commit(suggestion);
              }}>
              {suggestion}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
